/**
 * Estilos para a página de recursos.
 *
 * Este módulo contém os componentes estilizados utilizados para renderizar
 * a interface de visualização e gerenciamento de recursos, com layout em grid
 * dividido em três seções principais: categorias, lista de recursos e visualização de conteúdo.
 *
 * @module Resources.Styles
 */

import styled from '@emotion/styled';

/**
 * Componente estilizado para o contêiner principal da página.
 *
 * Define o layout grid com três colunas para as seções principais:
 * - Coluna 1 (20%): Lista de categorias
 * - Coluna 2 (30%): Lista de recursos
 * - Coluna 3 (50%): Visualização do conteúdo do recurso
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const Main = styled.main`
  display: grid;
  grid-template-columns: 20% 30% 50%;
  max-height: 100%;
  max-width: 100%;
  margin-top: 1em;
  background-color: #ffffff;
  box-shadow: -2px 1px 7px -6px rgba(0, 0, 0, 0.6);
  box-sizing: border-box;
`;

/**
 * Componente estilizado para a seção de categorias (primeira coluna).
 *
 * Define o layout grid para a seção de categorias com:
 * - Área de botões no topo
 * - Lista de categorias com rolagem
 * - Estilização para categorias selecionadas
 * - Personalização da barra de rolagem
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const SectionCategory = styled.section`
  display: grid;
  grid-template-rows: 3.5fr 16fr;
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);

  .buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
    justify-content: center;
    align-items: center;
    justify-items: center;
    box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
    padding: 7%;
  }

  .selected {
    color: #3b86ff;
    font-weight: 600;
  }

  .categories {
    padding: 10%;
    max-height: 84%;
    overflow-y: auto;

    button {
      display: flex;
      justify-content: space-between;
      border: none;
      cursor: pointer;
      text-align: left;
      background-color: transparent;
      padding: 10px;
      width: 100%;
      font-size: 0.7rem;
    }

    .category-name {
      flex-wrap: wrap;
      padding-right: 10px;
    }
  }

  .categories::-webkit-scrollbar {
    width: 4px;
  }

  .categories::-webkit-scrollbar-thumb {
    background: rgba(207, 207, 207, 0.64);
    border-radius: 4px;
  }
`;

/**
 * Componente estilizado para a seção de recursos (segunda coluna).
 *
 * Define o layout grid para a seção de recursos com:
 * - Cabeçalho com título
 * - Lista de cards de recursos com rolagem
 * - Estilização para cards de recursos
 * - Estados visuais para cards selecionados
 * - Cores de borda alternadas para os cards
 * - Personalização da barra de rolagem
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const SectionResource = styled.section`
  display: grid;
  grid-template-rows: 2fr 16fr;
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
  align-items: start;
  max-height: 100%;

  .title-header {
    flex-wrap: wrap;
    padding-block: 7%;
    padding-inline: 3%;
    font-size: 1.08rem;
    color: rgb(102, 102, 102);
  }

  .list-cards {
    max-height: 90%;
    overflow-y: auto;

    .loading {
      display: flex;
      justify-content: center;
      padding: 10%;
    }
  }

  .list-cards::-webkit-scrollbar {
    width: 4px;
  }

  .list-cards::-webkit-scrollbar-thumb {
    background: rgba(207, 207, 207, 0.64);
    border-radius: 4px;
  }

  .card-category {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding-block: 8%;
    padding-inline: 7%;
    background-color: #ffffff;
    gap: 5px;
    border: 0.5px solid rgba(207, 207, 207, 0.32);
    cursor: pointer;

    .info-card {
      display: flex;
      flex-direction: column;
    }

    .buttons-card {
      display: flex;
      align-items: center;
      gap: 10px;
      transition:
        transform 0.3s,
        box-shadow 0.3s;
      button {
        border: none;
        background-color: transparent;
        cursor: pointer;
      }
      button:hover {
        transform: translateY(-1.5px);
      }
    }

    h1 {
      font-size: 0.75rem;
    }

    p {
      font-size: 0.72rem;
    }
  }

  .cardSelected {
    background-color: #f1f1f3;
    border-left: 2px solid #ff6565 !important;
  }

  .card-category:nth-child(2n + 1) {
    border-left: 2px solid #a3a0fb;
  }

  .card-category:nth-child(2n + 2) {
    border-left: 2px solid #3b86ff;
  }
`;

/**
 * Componente estilizado para a seção de texto/conteúdo (terceira coluna).
 *
 * Define o layout grid para a seção de visualização de conteúdo com:
 * - Cabeçalho com título e ações
 * - Área de conteúdo com rolagem
 * - Estilização para o texto
 * - Personalização da barra de rolagem
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const SectionText = styled.section`
  display: grid;
  grid-template-rows: 2fr 16fr;
  max-width: 100%;
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);

  .text-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8%;
    font-size: 1rem;
    box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
  }

  .text-page {
    padding: 7%;
    max-height: 82%;
    overflow-y: auto;
  }

  p {
    font-size: 1rem;
    text-align: left;
    padding: 2%;
    margin-inline: 3%;
  }

  .text-page::-webkit-scrollbar {
    width: 5px;
  }

  .text-page::-webkit-scrollbar-thumb {
    background: rgba(207, 207, 207, 0.64);
    border-radius: 4px;
  }
`;

/**
 * Componente estilizado para o cabeçalho de modais.
 *
 * Define o estilo para o cabeçalho de modais com:
 * - Layout flexível com justificação entre extremos
 * - Borda inferior
 * - Estilização para título e botão de fechar
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const Header = styled.header`
  display: flex;
  padding: 5px;
  border-bottom: 1px solid rgba(207, 207, 207, 0.64);
  justify-content: space-between;

  .title-modal {
    font-size: 1rem;
  }

  button {
    border: none;
    background-color: transparent;
    cursor: pointer;
  }
`;

/**
 * Componente estilizado para o conteúdo de modais.
 *
 * Define o estilo para o conteúdo de diferentes tipos de modais:
 * - Layout flexível em coluna
 * - Estilização para botões de confirmação de exclusão
 * - Estilização para mensagens de alerta
 * - Estilização para formulários dentro do modal
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const ContentModal = styled.div`
  display: flex;
  flex-direction: column;
  width: 100;
  height: 100;

  .buttons-delete {
    display: flex;
    justify-content: center;
    gap: 40px;
    width: 100%;
  }

  .text-alert {
    width: 100%;
    text-align: center;
    padding-block: 60px;
  }

  form {
    display: flex;
    flex-direction: column;
    padding-top: 30px;
    gap: 50px;

    input {
      height: 2.2rem;
      width: 95%;
      border: 0.5px solid rgba(207, 207, 207, 0.69);
      border-radius: 2px;
      font-size: 0.8rem;
      color: rgb(68, 68, 68);
      padding-left: 15px;
      align-items: center;
      box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
      outline: none;
    }

    div {
      display: flex;
      justify-content: end;
    }
  }
`;

/**
 * Componente estilizado para o cabeçalho da seção de categorias.
 *
 * Define o estilo para o cabeçalho da seção de categorias com:
 * - Layout flexível centralizado verticalmente
 * - Padding interno
 * - Efeitos de transição para hover
 * - Estilização para botões de ação
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const HeaderCategory = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  padding-inline: 2%;
  transition:
    transform 0.3s,
    box-shadow 0.3s;

  .buttons-category {
    display: flex;
    align-items: center;
    padding-right: 8px;
    gap: 10px;
    transition:
      transform 0.3s,
      box-shadow 0.3s;
    button {
      border: none;
      background-color: transparent;
      cursor: pointer;
    }
    button:hover {
      transform: translateY(-1.5px);
    }
  }
`;

export const AlertContainer = styled.div`
  position: fixed;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start; /* Alinha os alertas ao início horizontalmente */
`;
